import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { ScrollReveal } from '@/components/ScrollReveal';
import BackToTop from '@/components/BackToTop';
import StickyReservationCTA from '@/components/StickyReservationCTA';
import UniformCard from '@/components/UniformCard';
import { MapPin, ExternalLink, Building2, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const ChoisirCamping = () => {
  const campings = [
    {
      id: 'atlantique-montalivet',
      name: 'Atlantique Montalivet',
      location: 'Montalivet, Gironde',
      description: 'Camping familial proche de l\'océan avec accès direct aux pistes cyclables.',
      image: './images/montalivet_beach_cycling_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/acm/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Proche océan']
    },
    {
      id: 'cote-sauvage',
      name: 'La Côte Sauvage',
      location: 'Côte Atlantique',
      description: 'Découvrez la beauté sauvage de la côte atlantique à vélo.',
      image: './images/ile_de_re_cycling_20260110_101615_20260117_112951.png',
      keiraLink: 'https://reservation.keria.app/lacotesauvage/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Nature préservée']
    },
    {
      id: 'grainetiere',
      name: 'La Grainetière',
      location: 'Vendée',
      description: 'Camping familial au cœur de la Vendée avec de nombreuses pistes cyclables.',
      image: './images/vendee_cycling_paths_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/lagrainetiere/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Pistes cyclables']
    },
    {
      id: 'pinede',
      name: 'La Pinède',
      location: 'Châteaufond',
      description: 'Camping ombragé dans un cadre naturel exceptionnel.',
      image: './images/lacanau_forest_cycling_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/lapinede/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Cadre naturel']
    },
    {
      id: 'loyada',
      name: 'Loyada',
      location: 'Côte Atlantique',
      description: 'Camping moderne avec services premium et accès vélo facilité.',
      image: './images/royan_coast_cycling_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/loyada',
      features: ['Camping partenaire', 'Réservation Keria', 'Services premium']
    },
    {
      id: 'aunis-club',
      name: 'Aunis Club',
      location: 'Charente-Maritime',
      description: 'Club de vacances avec activités vélo et découverte de la région.',
      image: './images/arcachon_basin_cycling_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/aunisclub',
      features: ['Camping partenaire', 'Réservation Keria', 'Activités vélo']
    },
    {
      id: 'clos-cotte',
      name: 'Clos Cotté',
      location: 'Côte Atlantique',
      description: 'Camping de charme dans un environnement préservé.',
      image: './images/ile_de_re_cycling_20260110_101615_20260117_112951.png',
      keiraLink: 'https://reservation.keria.app/closcotte/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Environnement préservé']
    },
    {
      id: 'coeur-vendee',
      name: 'Cœur de Vendée',
      location: 'Vendée',
      description: 'Au cœur de la Vendée, découvrez la région à vélo en famille.',
      image: './images/vendee_cycling_paths_20260110_101615_20260117_112952.png',
      keiraLink: 'https://reservation.keria.app/coeurvende/welcomepage',
      features: ['Camping partenaire', 'Réservation Keria', 'Découverte famille']
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary to-primary/90 text-primary-foreground py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-foreground/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <MapPin className="h-8 w-8" />
              </div>
              <h1 className="text-3xl lg:text-5xl font-bold mb-6">
                Choisir mon camping
              </h1>
              <p className="text-lg lg:text-xl text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto mb-8">
                Sélectionnez votre camping partenaire pour accéder directement à la réservation de vélos.
              </p>
              <Button asChild variant="secondary" size="lg" className="bg-white text-primary hover:bg-white/90">
                <Link to="/reserver">
                  <ArrowLeft className="mr-2 h-5 w-5" />
                  Retour aux options de réservation
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Campings Grid */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-4">
                Nos campings partenaires
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Chaque camping propose ses propres tarifs et conditions. Cliquez pour accéder à la réservation.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {campings.map((camping, index) => (
              <ScrollReveal key={camping.id} delay={index * 100}>
                <UniformCard
                  title={camping.name}
                  description={camping.description}
                  image={camping.image}
                  imageAlt={camping.name}
                  badges={[camping.location, ...camping.features]}
                  badgeColors={['bg-blue-100 text-blue-700', 'bg-slate-100 text-slate-700', 'bg-slate-100 text-slate-700', 'bg-slate-100 text-slate-700']}
                  ctaText="Réserver un vélo"
                  ctaLink={camping.keiraLink}
                  isExternal={true}
                  variant="image-top"
                />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Information complémentaire */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Votre camping n'apparaît pas ?
              </h3>
              <p className="text-slate-600 leading-relaxed mb-6">
                Contactez-nous pour connaître nos autres partenaires ou pour étudier une collaboration avec votre établissement.
              </p>
              <Button asChild variant="outline" size="lg">
                <Link to="/nous-contacter">
                  Nous contacter
                  <ArrowLeft className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <StickyReservationCTA />
      <BackToTop />
      <Footer />
    </div>
  );
};

export default ChoisirCamping;